import type { Distance, Point, SpatialRectangle } from "@pdfking/math"
import { Max, Min, Subtract, Total } from "@pdfking/math"

import type { Matrix, PageShape, PathPoint } from "#public"
import { PlotFunction } from "#public"

import { MatrixApply } from "../utils/MatrixApply"
import { MatrixCompose } from "../utils/MatrixCompose"
import { MatrixIdentity } from "../utils/MatrixIdentity"
import { ClipPolygon } from "./ClipPolygon"
import { DrawPath } from "./DrawPath"

// Steps a curved segment is cut in: a quarter circle a few hundredths off at most
const STEPS = 12

const same = (a: Point<Distance>, b: Point<Distance>) => a.x === b.x && a.y === b.y

// A closed path as a polygon: a straight segment its two ends, a curve the
// cubic Bézier through its handles, sampled
const flatten = (points: PathPoint[]): Point<Distance>[] =>
  points.flatMap((a, i) => {
    const b = points[(i + 1) % points.length]!
    if (same(a, a.after) && same(b, b.before)) return [{ x: a.x, y: a.y }]
    return Array.from({ length: STEPS }, (_, step) => {
      const t = step / STEPS
      const weights = [(1 - t) ** 3, 3 * (1 - t) ** 2 * t, 3 * (1 - t) * t ** 2, t ** 3]
      const along = (axis: "x" | "y") =>
        [a[axis], a.after[axis], b.before[axis], b[axis]].reduce(
          (sum, value, k) => sum + value * weights[k]!,
          0
        ) as Distance
      return { x: along("x"), y: along("y") }
    })
  })

const corners = ({ x, y, width, height }: SpatialRectangle<Distance>): PathPoint[] =>
  DrawPath([
    { x, y },
    { x: Total(x, width), y },
    { x: Total(x, width), y: Total(y, height) },
    { x, y: Total(y, height) },
  ])

const moved = (matrix: Matrix<Distance>, points: PathPoint[]): PathPoint[] =>
  points.map(point => ({
    ...MatrixApply(matrix, point),
    after: MatrixApply(matrix, point.after),
    before: MatrixApply(matrix, point.before),
  }))

const path = (shape: PageShape, points: Point<Distance>[]): PageShape[] =>
  points.length < 3
    ? []
    : [{ ...shape, closed: true, enum: PlotFunction.PLOT_PATH, points: DrawPath(points) }]

const clip = (
  shapes: PageShape[],
  box: SpatialRectangle<Distance>,
  matrix: Matrix<Distance>
): PageShape[] =>
  shapes.flatMap((shape): PageShape[] => {
    // Only moved, not turned nor scaled: a rect stays a rect
    const [a, b, c, d] = matrix
    const turned = a !== 1 || b !== 0 || c !== 0 || d !== 1
    if (shape.enum === PlotFunction.PLOT_GROUP)
      return clip(shape.shapes, box, MatrixCompose(shape.matrix ?? MatrixIdentity(), matrix))
    if (shape.enum === PlotFunction.PLOT_RECT && shape.position && !turned) {
      const { x, y } = MatrixApply(matrix, shape.position)
      const position = { ...shape.position, x, y }
      const left = Max(position.x, box.x)
      const bottom = Max(position.y, box.y)
      const right = Min(Total(position.x, position.width), Total(box.x, box.width))
      const top = Min(Total(position.y, position.height), Total(box.y, box.height))
      const width = Subtract(right, left)
      const height = Subtract(top, bottom)
      return width > 0 && height > 0
        ? [{ ...shape, position: { x: left, y: bottom, width, height } }]
        : []
    }
    if (shape.enum === PlotFunction.PLOT_RECT && shape.position) {
      const { position: _, ...rest } = shape
      return path(
        rest as PageShape,
        ClipPolygon(flatten(moved(matrix, corners(shape.position))), box)
      )
    }
    if (shape.enum === PlotFunction.PLOT_PATH && shape.closed && shape.points)
      return path(shape, ClipPolygon(flatten(moved(matrix, shape.points)), box))
    // What cannot be cut (a text, a media, an open line) stays whole, where
    // its groups put it
    return [{ enum: PlotFunction.PLOT_GROUP, matrix, shapes: [shape] }]
  })

/** `shapes` cut to `box`, as a clip would show them: rects and closed paths
 * to the part of them inside it, a curve sampled first; groups resolved, so
 * a turned rect is cut as the shape it turns into. What has no area to cut
 * (a text, a media, an open line) is kept whole. */
export const ClipShapes = ({
  shapes,
  box,
}: {
  shapes: PageShape[]
  box: SpatialRectangle<Distance>
}): PageShape[] => clip(shapes, box, MatrixIdentity())
